import Modal from 'components/Modal';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import ReoderBookingForm from './ReoderBookingForm';
import { Form } from 'antd';
import shopSelectors from 'services/shop/selectors';
import { useAppDispatch } from 'store/hooks';
import { find, get } from 'lodash';
import { ISortStaffV2 } from 'features/settingStaff/services/types/api';
import { IResponseDataBody } from 'services/response';
import settingApis from 'features/settingStaff/services/apis';
import toast from 'utils/toast';
import shopActions from 'services/shop/actions';
import settingStaffActions from 'features/settingStaff/services/actions';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import appointmentActions from 'features/appointment/services/actions';
import appointmentSelectors from 'features/appointment/services/selectors';

export type ReoderBookingRef = {
  open: () => void;
  close: () => void;
};

export type IReoderListItemType = {
  id: string;
  name: string;
  avatar: string;
  type: string;
  activeBooking?: boolean;
};
type ReoderBookingProps = {};

type FormType = {
  items: IReoderListItemType[];
};

const ReoderBooking = forwardRef<ReoderBookingRef, ReoderBookingProps>((props, ref) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const setLoading = useSetLoadingPage();
  const staffList = shopSelectors.data.staffs();
  const staffsAvailableBooking = appointmentSelectors.getListStaffAvailableBooking();
  const staffSize: number = appointmentSelectors.staffSize();
  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    const staffsAvailableOrder = staffsAvailableBooking.map(s => s?.id?.toString());
    const staffListSortByAvailable = [...staffList].sort((a, b) => {
      const aIdx = staffsAvailableOrder.indexOf(a?.id?.toString());
      const bIdx = staffsAvailableOrder.indexOf(b?.id?.toString());
      if (aIdx === -1 && bIdx === -1) return 0;
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });

    form.setFieldValue(
      'items',
      staffListSortByAvailable
        .map((o) => ({
          id: o.id,
          name: o.nickName || o.firstName,
          avatar: o.urlImage,
          type: o.staffType,
          activeBooking: !!find(
            staffsAvailableBooking,
            (s) => s?.id?.toString() === o?.id?.toString()
          ),
        }))
    );
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: () => setOpen(false),
  }));

  const handleSubmit = async (values: FormType) => {
    const items: IReoderListItemType[] = values.items;
    setLoading(true);
    const body: ISortStaffV2[] = items.map((o) => ({
      enableBookingOnline: o.activeBooking || false,
      staffId: o.id,
    }));
    try {
      const res: IResponseDataBody<boolean> = await settingApis.sortStaffV2(body);
      if (res.data.data) {
        toast.success('Reorder successful');
        dispatch(appointmentActions.getAppointmentMasterData.fetch({ size: staffSize === 10 ? 999 : staffSize }));
        const params = {
          page: 1,
          size: 20,
        };
        dispatch(settingStaffActions.setParams(params));
        setOpen(false);
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg || 'Reorder failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={open}
      width={900}
      noneBodyStyle
      v2
      onClose={handleClose}
      onSubmit={() => form.submit()}
      modalTitle='Reorder booking'
    >
      <Form form={form} onFinish={handleSubmit}>
        <Form.List name='items'>
          {(fields, { move }) => {
            return <ReoderBookingForm fields={fields} move={move} />;
          }}
        </Form.List>
      </Form>
    </Modal>
  );
});
ReoderBooking.displayName = 'ReoderBooking';

export default ReoderBooking;
